import React, { useState, useEffect } from 'react';
import { Modal, Spinner, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { plansList, addonsList, createCheckoutSession, getCompanySubscription, updateSubscriptionAddons, updateSubscriptionPlan, cancelSubscription, previewInvoice } from '../api/subscription';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import BespokeEnquiryModal from './BespokeEnquiryModal';

const SubscriptionPlansModal = ({ show, onClose, companySubscription: initialSubscription = null, companyId: propCompanyId = null, initialViewMode = null }) => {
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [viewMode, setViewMode] = useState('plans'); // 'plans' or 'details'
  const [subscription, setSubscription] = useState(initialSubscription);                                  
  const [managingAddons, setManagingAddons] = useState(false);
  const [selectedAddonsToManage, setSelectedAddonsToManage] = useState([]);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  const [showBespokeEnquiry, setShowBespokeEnquiry] = useState(false);
  const [showAddonSuccessModal, setShowAddonSuccessModal] = useState(false);
  const [addedAddonNames, setAddedAddonNames] = useState([]);
  const [nextInvoice, setNextInvoice] = useState(null);

  const storeCompanyId = useSelector((store) => store.main.company_id);
  const authCompanyId = useSelector((store) => store.auth?.company_id);
  const userType = useSelector((store) => store.auth?.user_type);
  const company_id = propCompanyId || authCompanyId || storeCompanyId;
  const isCompanyUser = userType?.toLowerCase() === 'company';

  useEffect(() => {
    if (show) {
      setManagingAddons(false);
      if (initialSubscription) {
        setSubscription(initialSubscription);
        setViewMode(initialViewMode || 'details');
      } else {
        setSubscription(null);
        setViewMode(initialViewMode || 'plans');
      }

      if (initialViewMode === 'plans' || !initialSubscription) {
        fetchPlans();
      }

      if (initialViewMode === 'details' && initialSubscription?.addons) {
        setSelectedAddonsToManage(initialSubscription.addons.filter(a => !a.scheduled_for_removal).map(a => a.addon_id));
      } else {
        setSelectedAddonsToManage([]);
      }
    }
  }, [show, initialSubscription, initialViewMode]);

  useEffect(() => {
    setNextInvoice(null);
    const isCancelled = subscription?.cancelled_at || ['cancelled', 'canceled'].includes(subscription?.status?.toLowerCase());
    if (!isCancelled && subscription?.stripe_customer_id && subscription?.stripe_subscription_id) {
      let cancelled = false;
      const fetchPreview = async () => {
        try {
          const res = await previewInvoice(subscription.stripe_customer_id, subscription.stripe_subscription_id);
          if (!cancelled && res?.code === 1) {
            setNextInvoice(res.data);
          }
        } catch (err) {
          console.error('Error fetching invoice preview:', err);
        }
      };
      fetchPreview();
      return () => { cancelled = true; };
    }
  }, [subscription?.stripe_customer_id, subscription?.stripe_subscription_id, subscription?.status, subscription?.cancelled_at, subscription?.plan_id, subscription?.pending_plan_id]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const [plansRes, addonsRes] = await Promise.all([
        plansList({ page: 1, limit: 100 }),
        addonsList({ page: 1, limit: 100 })
      ]);
      
      if (plansRes?.code === 1) {
        let allPlans = plansRes.data?.plans || [];
        
        // Filter plans based on company subscription
        if (company_id) {
          // Company has subscription - show only company-specific plans
          const companyPlans = allPlans.filter(plan => 
            plan.plan_type === 'custom' && plan.company_id === company_id
          );
          
          if (companyPlans.length > 0) {
            // Show company-specific plans
            setPlans(companyPlans);
            console.log('Showing company-specific plans:', companyPlans);
            
            // Auto-select if only one plan
            if (companyPlans.length === 1) {
              handlePlanSelection(companyPlans[0]);
              console.log('Auto-selected company plan:', companyPlans[0]);
            }
          } else {
            // No company-specific plans, show general plans
            const generalPlans = allPlans.filter(plan => plan.plan_type === 'general');
            setPlans(generalPlans);
            console.log('No company-specific plans found, showing general plans:', generalPlans);
            
            // Auto-select if only one plan
            if (generalPlans.length === 1) {
              handlePlanSelection(generalPlans[0]);
              console.log('Auto-selected general plan:', generalPlans[0]);
            }
          }
        } else {
          // No company subscription - show all general plans
          const generalPlans = allPlans.filter(plan => plan.plan_type === 'general');
          setPlans(generalPlans);
          console.log('No company ID, showing general plans:', generalPlans);
          
          // Auto-select if only one plan
          if (generalPlans.length === 1) {
            handlePlanSelection(generalPlans[0]);
            console.log('Auto-selected general plan:', generalPlans[0]);
          }
        }
      }
      if (addonsRes?.code === 1) {
        setAddons(addonsRes.data?.addons || []);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddonToggle = (addonId) => {
    setSelectedAddons(prev => {
      const newSelection = prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId];
      console.log('Addon toggled:', addonId, 'New selection:', newSelection);
      return newSelection;
    });
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setTermsAccepted(false);
    // Clear addon selections when switching plans
    setSelectedAddons([]);
    // If plan has predefined addons and is a custom plan, auto-select them
    if (plan.addons && plan.addons.length > 0 && plan.company_id > 0) {
      setSelectedAddons(plan.addons);
    }
  };

  const handleBespokeEnquiryClick = () => {
    onClose();
    setShowBespokeEnquiry(true);
  };

  const handleAddonSuccessOk = () => {
    setShowAddonSuccessModal(false);
    // Only redirect to fee-settings if there are active addons
    if (addedAddonNames.length > 0) {
      window.location.href = '/fee-settings';
    } else {
      window.location.reload();
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?\n\nYour subscription will be cancelled at the end of the current billing period, and you can continue to use all features until then.\n\nClick OK to proceed or Cancel to keep your subscription active.')) {
      return;
    }

    try {
      const response = await cancelSubscription({
        company_id,
        stripe_subscription_id: subscription.stripe_subscription_id
      });

      if (response?.code === 1) {
        toast.success(response.message || 'Subscription cancelled successfully. You can continue to use it until the end of the billing period.');
        setSubscription({
          ...subscription,
          cancelled_at: response.data?.cancelled_at
        });
      } else {
        toast.error(response?.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Error cancelling subscription');
    }
  };

  const switchToPlansView = () => {
    setViewMode('plans');
    fetchPlans();
  };

  const toggleAddonManagement = async () => {
    setManagingAddons(!managingAddons);
    if (!managingAddons) {
      // Fetch addons if not already loaded
      if (addons.length === 0) {
        await fetchPlans(); // This fetches both plans and addons
      }
      // Initialize selected addons with current subscription addons
      // (addons scheduled for removal are unchecked so re-checking them
      // cancels the scheduled removal)
      const currentAddonIds = subscription.addons?.filter(a => !a.scheduled_for_removal).map(addon => addon.addon_id) || [];
      setSelectedAddonsToManage(currentAddonIds);
    }
  };

  const handleAddonToggleForSubscription = (addonId) => {
    setSelectedAddonsToManage(prev => {
      if (prev.includes(addonId)) {
        return prev.filter(id => id !== addonId);
      } else {
        return [...prev, addonId];
      }
    });
  };

  const handleUpdateSubscriptionAddons = async () => {
    if (!company_id) {
      toast.error('Company ID not found');
      return;
    }

    setUpdatingSubscription(true);
    try {
      // Get current addon IDs (excluding ones already scheduled for removal,
      // so re-selecting them restores them instead of re-scheduling)
      const currentAddonIds = subscription.addons?.filter(a => !a.scheduled_for_removal).map(addon => addon.addon_id) || [];
      
      // Find addons to add and remove
      const addonsToAdd = selectedAddonsToManage.filter(id => !currentAddonIds.includes(id));
      const addonsToRemove = currentAddonIds.filter(id => !selectedAddonsToManage.includes(id));

      console.log('Current addons:', currentAddonIds);
      console.log('Selected addons:', selectedAddonsToManage);
      console.log('Addons to add:', addonsToAdd);
      console.log('Addons to remove:', addonsToRemove);

      if (addonsToAdd.length === 0 && addonsToRemove.length === 0) {
        toast.info('No changes to addons');
        return;
      }

      // Call API to update subscription
      const result = await updateSubscriptionAddons({
        company_id: company_id,
        subscription_id: subscription.stripe_subscription_id,
        addons_to_add: addonsToAdd,
        addons_to_remove: addonsToRemove
      });
      
      if (result.code === 1) {
        // Get names of all active addons after update
        const updatedSubscription = await getCompanySubscription(company_id);
        if (updatedSubscription?.code === 1) {
          setSubscription(updatedSubscription.data);
          
          // Get all active addon names
          const activeAddonNames = (updatedSubscription.data.addons || []).map(addon => {
            const addonDetails = addons.find(a => a.id === addon.addon_id);
            return addonDetails ? addonDetails.addon_name : `Addon ${addon.addon_id}`;
          });
          
          setAddedAddonNames(activeAddonNames);
        } else {
          setAddedAddonNames([]);
        }
        
        setShowAddonSuccessModal(true);
        
        // Close the subscription plan modal
        onClose();
        
        setManagingAddons(false);
      } else {
        toast.error(result.message || 'Failed to update subscription addons');
      }
    } catch (error) {
      console.error('Error updating subscription addons:', error);
      toast.error('Error updating subscription addons');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const isChangingExistingPlan = Boolean(subscription?.stripe_subscription_id && viewMode === 'plans');

  const handlePurchase = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan first');
      return;
    }

    if (!company_id) {
      toast.error('Company ID not found');
      return;
    }

    if (isChangingExistingPlan) {
      const currentPlanId = subscription.plan_id ?? subscription.plan?.id;
      if (currentPlanId != null && Number(currentPlanId) === Number(selectedPlan.id)) {
        toast.info('This is already your current plan');
        return;
      }
    }

    setPurchasing(true);
    try {
      if (isChangingExistingPlan) {
        const response = await updateSubscriptionPlan({
          company_id,
          plan_id: selectedPlan.id,
          stripe_subscription_id: subscription.stripe_subscription_id
        });

        if (response?.code === 1) {
          const { effective_immediately, effective_date } = response.data || {};
          
          if (effective_immediately === false) {
            // Downgrade - scheduled for next billing cycle
            const effectiveDate = effective_date ? new Date(effective_date).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            }) : 'next billing cycle';
            
            toast.success(
              <div>
                <strong>Plan Downgrade Scheduled</strong>
                <br />
                Your subscription will be downgraded to <strong>{selectedPlan.plan_name}</strong> on <strong>{effectiveDate}</strong>.
                <br />
                You will continue to have access to your current plan features until then.
              </div>,
              { autoClose: 8000 }
            );
          } else {
            // Upgrade or same price - immediate effect
            toast.success(
              <div>
                <strong>Plan Updated Successfully</strong>
                <br />
                Your subscription has been upgraded to <strong>{selectedPlan.plan_name}</strong>.
                <br />
                The changes are effective immediately.
              </div>,
              { autoClose: 5000 }
            );
          }
          
          const updatedSubscription = await getCompanySubscription(company_id);
          if (updatedSubscription?.code === 1) {
            setSubscription(updatedSubscription.data);
          }
          setViewMode('details');
        } else {
          toast.error(response?.message || 'Failed to update plan');
        }
        return;
      }

      const payload = {
        plan_id: selectedPlan.id,
        addon_ids: selectedAddons,
        company_id: company_id
      };

      console.log('Purchase payload:', payload);
      console.log('Selected addons count:', selectedAddons.length);
      console.log('Selected addon IDs:', selectedAddons);

      const response = await createCheckoutSession(payload);
      
      if (response?.code === 1 && response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error(response?.message || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error processing subscription:', error);
      toast.error('An error occurred while processing your request');
    } finally {
      setPurchasing(false);
    }
  };

  const scrollPlans = (direction) => {
    const container = document.getElementById('plans-scroll-container');
    if (container) {
      const scrollAmount = 350;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {viewMode === 'details'
            ? 'Your Subscription Details'
            : isChangingExistingPlan
              ? 'Change subscription plan'
              : 'Choose Your Subscription Plan'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '550px', paddingTop: '20px' }}>
        {viewMode === 'details' && subscription ? (
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="card shadow-lg border-0" style={{ borderRadius: '15px' }}>
                  <div className="card-body p-5">
                    <div className="text-center mb-4">
                      <div className="mb-3">
                        <i className="fa fa-check-circle text-success" style={{ fontSize: '60px' }}></i>
                      </div>
                      <h3 className="text-success mb-2">Active Subscription</h3>
                      <div className="badge bg-primary fs-6 mb-3">{subscription.status}</div>
                    </div>

                    <div className="subscription-details">
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <h5 className="mb-3" style={{ color: '#2b61b1' }}>
                            <i className="fa fa-crown me-2"></i>
                            {subscription.plan?.plan_name || 'Subscription Plan'}
                          </h5>
                          <div className="mb-2">
                            <strong>Price:</strong> £{parseFloat(subscription.plan?.price || 0).toFixed(2)}/month
                          </div>
                          <div className="mb-2">
                            <strong>Student Limit:</strong> {subscription.plan?.student_limit || 'Unlimited'} students
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2">
                            <strong>Subscription ID:</strong> {subscription.stripe_subscription_id || 'N/A'}
                          </div>
                          <div className="mb-2">
                            <strong>Started:</strong> {subscription.current_period_start ? (() => {
                              const date = new Date(subscription.current_period_start);
                              const day = date.getDate().toString().padStart(2, '0');
                              const month = (date.getMonth() + 1).toString().padStart(2, '0');
                              const year = date.getFullYear();
                              return `${day}/${month}/${year}`;
                            })() : 'N/A'}
                          </div>
                          <div className="mb-2">
                            <strong>{(subscription?.cancelled_at || ['cancelled', 'canceled'].includes(subscription?.status?.toLowerCase())) ? 'Cancellation Date:' : 'Next Billing:'}</strong> {(() => {
                              const cancellationDate = subscription?.cancelled_at || subscription.current_period_end;
                              if (cancellationDate) {
                                const date = new Date(cancellationDate);
                                const day = date.getDate().toString().padStart(2, '0');
                                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                const year = date.getFullYear();
                                const formatted = `${day}/${month}/${year}`;
                                if (subscription?.cancelled_at || ['cancelled', 'canceled'].includes(subscription?.status?.toLowerCase())) {
                                  return `This subscription will be cancelled at ${formatted}`;
                                }
                                return subscription.pending_plan_id
                                  ? `${formatted} (${subscription.pendingPlan?.plan_name || 'Unknown Plan'})`
                                  : formatted;
                              }
                              return 'N/A';
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Plan Summary */}
                      <div className="row mb-4">
                        <div className="col-12">
                          <div className="card" style={{ backgroundColor: '#f8faff', borderColor: '#d4e1f5', borderRadius: '10px', borderWidth: '1px' }}>
                            <div className="card-body p-4">
                              <h6 className="card-title mb-3" style={{ color: '#2b61b1', fontSize: '0.95rem', fontWeight: 600 }}>
                                <i className="fa fa-exchange me-2"></i>
                                Plan Summary
                              </h6>
                              <div className="row">
                                <div className="col-md-6">
                                  <div className="mb-2">
                                    <label className="form-label mb-0" style={{ fontSize: '0.8rem', color: '#495057', fontWeight: 500 }}>Current Plan</label>
                                    <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem', color: '#2b61b1', fontWeight: 600 }}>{subscription.plan?.plan_name || 'N/A'}</p>
                                  </div>
                                  <div className="mb-2">
                                    <label className="form-label mb-0" style={{ fontSize: '0.8rem', color: '#495057', fontWeight: 500 }}>Next / Scheduled Plan</label>
                                    <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem', color: '#2b61b1', fontWeight: 600 }}>
                                      {(subscription?.cancelled_at || ['cancelled', 'canceled'].includes(subscription?.status?.toLowerCase()))
                                        ? 'N/A (subscription cancelled)'
                                        : subscription.pending_plan_id
                                          ? (subscription.pendingPlan?.plan_name || 'Unknown Plan')
                                          : (subscription.plan?.plan_name || 'N/A')}
                                    </p>
                                  </div>
                                  <div className="mb-2 mb-md-0">
                                    <label className="form-label mb-1" style={{ fontSize: '0.8rem', color: '#495057', fontWeight: 500 }}>Request Date</label>
                                    <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem', color: '#2b61b1', fontWeight: 600 }}>
                                      {subscription.updatedAt ? moment(subscription.updatedAt).format('MMMM DD, YYYY') : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  {(() => {
                                    const isCancelled = subscription?.cancelled_at || ['cancelled', 'canceled'].includes(subscription?.status?.toLowerCase());
                                    const effectiveDate = isCancelled ? subscription.cancelled_at : subscription.pending_plan_effective_date;
                                    if (!effectiveDate) return null;
                                    return (
                                      <div className="mb-2">
                                        <label className="form-label mb-0" style={{ fontSize: '0.8rem', color: '#495057', fontWeight: 500 }}>Effective Date</label>
                                        <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem', color: '#2b61b1', fontWeight: 600 }}>
                                          {moment(effectiveDate).format('MMMM DD, YYYY')}
                                        </p>
                                      </div>
                                    );
                                  })()}
                                  <div className="mb-0">
                                    <label className="form-label mb-0" style={{ fontSize: '0.8rem', color: '#495057', fontWeight: 500 }}>Next Billing Date</label>
                                    <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem', color: '#2b61b1', fontWeight: 600 }}>
                                      {(subscription?.cancelled_at || ['cancelled', 'canceled'].includes(subscription?.status?.toLowerCase())) ? 'N/A' : (subscription.current_period_end ? moment(subscription.current_period_end).format('MMMM DD, YYYY') : 'N/A')}
                                    </p>
                                  </div>
                                  <div className="mb-0">
                                    <label className="form-label mb-0" style={{ fontSize: '0.8rem', color: '#495057', fontWeight: 500 }}>Next Billing Amount</label>
                                    <div className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem', color: '#2b61b1', fontWeight: 600 }}>
                                      {(subscription?.cancelled_at || ['cancelled', 'canceled'].includes(subscription?.status?.toLowerCase()))
                                        ? 'N/A'
                                        : (() => {
                                            const basePlan = subscription.pending_plan_id ? subscription.pendingPlan : subscription.plan;
                                            const allAddons = subscription.addons || [];
                                            const paidAddons = allAddons.filter(a => a.stripe_subscription_item_id && !a.scheduled_for_removal);
                                            const basePrice = parseFloat(basePlan?.price || 0);
                                            const addonsTotal = paidAddons.reduce((sum, a) => sum + parseFloat(a.addon?.price || 0), 0);
                                            const computedTotal = basePrice + addonsTotal;
                                            const currencySymbol = nextInvoice?.currency?.toLowerCase() === 'usd' ? '$' : '£';
                                            const displayAmount = nextInvoice ? parseFloat(nextInvoice.amount) : computedTotal;
                                            const breakdownTooltip = (
                                              <Tooltip id="next-billing-breakdown-tooltip" style={{
                                                  '--bs-tooltip-max-width': '320px',
                                                  '--bs-tooltip-font-size': '0.9rem',
                                                  '--bs-tooltip-padding-x': '12px',
                                                  '--bs-tooltip-padding-y': '12px',
                                                  '--bs-tooltip-bg': '#fff',
                                                  '--bs-tooltip-color': '#212529',
                                                  backgroundColor: '#fff',
                                                  color: '#212529',
                                                  border: '1px solid #dee2e6',
                                                  borderRadius: '8px'
                                                }}>
                                                <div className="text-start" style={{ minWidth: '180px' }}>
                                                  {nextInvoice?.lines?.length > 0 ? (
                                                    nextInvoice.lines.map((line, idx) => (
                                                      <div key={idx} className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                                                        <span>{line.description || `Line ${idx + 1}`}</span>
                                                        <span>{currencySymbol}{parseFloat(line.amount || 0).toFixed(2)}</span>
                                                      </div>
                                                    ))
                                                  ) : (
                                                    <>
                                                      <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                                                        <span>{basePlan?.plan_name || 'Plan'}</span>
                                                        <span>{currencySymbol}{basePrice.toFixed(2)}</span>
                                                      </div>
                                                      {allAddons.map((a, idx) => {
                                                        const addonPrice = a.stripe_subscription_item_id ? parseFloat(a.addon?.price || 0) : 0;
                                                        return (
                                                          <div key={a.id || idx} className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                                                            <span>{a.addon?.addon_name || `Addon ${idx + 1}`}</span>
                                                            <span>{currencySymbol}{addonPrice.toFixed(2)}</span>
                                                          </div>
                                                        );
                                                      })}
                                                    </>
                                                  )}
                                                  <div className="d-flex justify-content-between border-top pt-1 mt-1" style={{ fontSize: '0.8rem', fontWeight: 700 }}>
                                                    <span>Total</span>
                                                    <span>{currencySymbol}{displayAmount.toFixed(2)}</span>
                                                  </div>
                                                </div>
                                              </Tooltip>
                                            );
                                            return (
                                              <div>
                                                <span>{currencySymbol}{displayAmount.toFixed(2)}/month</span>
                                                {allAddons.length > 0 && (
                                                  <OverlayTrigger trigger="click" placement="right" rootClose overlay={breakdownTooltip}>
                                                    <a href="#" style={{ fontSize: '0.75rem', color: '#2b61b1', textDecoration: 'underline', fontWeight: 500, marginLeft: '8px' }} onClick={(e) => e.preventDefault()}>View breakdown</a>
                                                  </OverlayTrigger>
                                                )}
                                              </div>
                                            );
                                          })()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border-top pt-3">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">
                              <i className="fa fa-puzzle-piece me-2"></i>
                              Active Add-ons
                            </h6>
                            {subscription.plan?.plan_type !== 'custom' && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={toggleAddonManagement}
                                style={{ 
                                  borderColor: '#2b61b1', 
                                  color: '#2b61b1',
                                  backgroundColor: 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#2b61b1';
                                  e.target.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = '#2b61b1';
                                }}
                              >
                                <i className={`fa ${managingAddons ? 'fa-times' : 'fa-edit'} me-1`}></i>
                                {managingAddons ? 'Cancel' : 'Manage Add-ons'}
                              </Button>
                            )}
                          </div>

                          {managingAddons ? (
                            <div>
                              <style>{`
                                .form-check-input:checked {
                                  background-color: #2b61b1 !important;
                                  border-color: #2b61b1 !important;
                                }
                                .form-check-input:focus {
                                  border-color: #2b61b1 !important;
                                  box-shadow: 0 0 0 0.25rem rgba(78, 115, 223, 0.25) !important;
                                }
                              `}</style>
                              <div className="row mb-3">
                                {addons.filter(a => a.is_active).sort((a, b) => {
                                  const aIsFree = subscription.addons?.find(sa => sa.addon_id === a.id)?.stripe_subscription_item_id === null;
                                  const bIsFree = subscription.addons?.find(sa => sa.addon_id === b.id)?.stripe_subscription_item_id === null;
                                  // Free addons (true) should come first
                                  return (bIsFree ? 1 : 0) - (aIsFree ? 1 : 0);
                                }).map((addon) => {
                                  const isCurrentlySubscribed = subscription.addons?.some(sa => sa.addon_id === addon.id);
                                  const subscribedAddon = subscription.addons?.find(sa => sa.addon_id === addon.id);
                                  const isScheduledForRemoval = !!subscribedAddon?.scheduled_for_removal;
                                  const isSelected = selectedAddonsToManage.includes(addon.id);
                                  return (
                                    <div key={addon.id} className="col-md-6 mb-2">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={`manage-addon-${addon.id}`}
                                          checked={isSelected}
                                          onChange={() => handleAddonToggleForSubscription(addon.id)}
                                          disabled={isCurrentlySubscribed && subscription.addons?.find(sa => sa.addon_id === addon.id)?.stripe_subscription_item_id === null}
                                        />
                                        <label
                                          className="form-check-label d-flex justify-content-between align-items-center w-100"
                                          htmlFor={`manage-addon-${addon.id}`}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          <span>
                                            {addon.addon_name}
                                            {isScheduledForRemoval && (
                                              <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.7rem' }}>
                                                <i className="fa fa-clock me-1"></i>
                                                Cancels {subscribedAddon?.removal_effective_date ? moment(subscribedAddon.removal_effective_date).format('DD MMM YYYY') : 'at period end'}
                                              </span>
                                            )}
                                          </span>
                                          {isCurrentlySubscribed && subscription.addons?.find(sa => sa.addon_id === addon.id)?.stripe_subscription_item_id === null ? (
                                            <span className="badge" style={{ backgroundColor: '#e7f1ff', color: '#2b61b1' }}>FREE</span>
                                          ) : (
                                            <span className="badge" style={{ backgroundColor: '#2b61b1' }}>£{parseFloat(addon.price || 0).toFixed(0)}</span>
                                          )}
                                        </label>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={toggleAddonManagement}
                                  disabled={updatingSubscription}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={handleUpdateSubscriptionAddons}
                                  disabled={updatingSubscription}
                                  style={{ backgroundColor: '#2b61b1', borderColor: '#2b61b1' }}
                                >
                                  {updatingSubscription ? (
                                    <>
                                      <Spinner animation="border" size="sm" className="me-2" />
                                      Updating...
                                    </>
                                  ) : (
                                    'Update Subscription'
                                  )}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {subscription.addons && subscription.addons.length > 0 ? (
                                <div className="row">
                                  {subscription.addons.map((addon) => (
                                    <div key={addon.id} className="col-md-6 mb-2">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span>{addon.addon?.addon_name || 'Addon'}</span>
                                        {addon.scheduled_for_removal && (
                                          <span className="badge bg-warning text-dark">
                                            <i className="fa fa-clock me-1"></i>
                                            Cancels {addon.removal_effective_date ? moment(addon.removal_effective_date).format('DD MMM YYYY') : 'at period end'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-muted mb-0">No active add-ons</p>
                              )}
                            </div>
                          )}
                        </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading plans...</p>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">No subscription plans available.</p>
          </div>
        ) : (
          <>
            <style>{`
              .form-check-input:checked {
                background-color: #2b61b1 !important;
                border-color: #2b61b1 !important;
              }
              .form-check-input:focus {
                border-color: #2b61b1 !important;
                box-shadow: 0 0 0 0.25rem rgba(78, 115, 223, 0.25) !important;
              }
              .form-check-input:checked {
                background-color: #2b61b1 !important;
                border-color: #2b61b1 !important;
              }
              .form-check-input:focus {
                border-color: #2b61b1 !important;
                box-shadow: 0 0 0 0.25rem rgba(78, 115, 223, 0.25) !important;
              }
            `}</style>
            {/* Plans Section */}
            <div className="position-relative mb-4">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-light border-0 shadow-sm me-2"
                  onClick={() => scrollPlans('left')}
                  style={{ minWidth: '40px', height: '40px', borderRadius: '50%' }}
                >
                  <i className="fa fa-chevron-left"></i>
                </button>
                <div
                  id="plans-scroll-container"
                  className="d-flex overflow-auto flex-nowrap flex-grow-1"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', paddingTop: '10px', paddingBottom: '10px', paddingLeft: '5px' }}
                >
                  <style>{`
                    #plans-scroll-container::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>
                  {plans.filter(p => p.is_active).map((plan) => {
                    const isCurrentPlan = isChangingExistingPlan && 
                      Number(subscription.plan_id ?? subscription.plan?.id) === Number(plan.id);
                    
                    return (
                    <div
                      key={plan.id}
                      className="flex-shrink-0 me-3"
                      style={{ width: '320px' }}
                      onClick={() => handlePlanSelection(plan)}
                    >
                      <div
                        className={`card h-100 shadow-sm ${
                          selectedPlan?.id === plan.id ? '' : 'border-0'
                        }`}
                        style={{
                          borderRadius: '12px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          borderWidth: (selectedPlan?.id === plan.id || isCurrentPlan) ? '3px' : '1px',
                          borderStyle: 'solid',
                          borderColor: isCurrentPlan ? '#28a745' : (selectedPlan?.id === plan.id ? '#2b61b1' : 'transparent'),
                          transition: 'all 0.3s ease',
                          transform: selectedPlan?.id === plan.id ? 'scale(1.02)' : 'scale(1)',
                          backgroundColor: isCurrentPlan ? '#f0fff4' : 'white'
                        }}
                      >
                        {isCurrentPlan && (
                          <div
                            className="position-absolute top-0 end-0 m-2 text-white rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '30px', height: '30px', zIndex: 10, backgroundColor: '#28a745' }}
                          >
                            <i className="fa fa-check"></i>
                          </div>
                        )}
                        {selectedPlan?.id === plan.id && !isCurrentPlan && (
                          <div
                            className="position-absolute top-0 end-0 m-2 text-white rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '30px', height: '30px', zIndex: 10, backgroundColor: '#2b61b1' }}
                          >
                            <i className="fa fa-check"></i>
                          </div>
                        )}
                        <div className="card-body d-flex flex-column p-4">
                          <h5 className="card-title fw-bold mb-2" style={{ color: '#333' }}>
                            {plan.plan_name}
                            {isCurrentPlan && (
                              <span className="badge ms-2" style={{ backgroundColor: '#28a745', color: 'white' }}>Current</span>
                            )}
                          </h5>
                          {plan.description && (
                            <p className="card-text text-muted small mb-3" style={{ maxHeight: '60px', overflowY: 'auto' }}>
                              {plan.description}
                            </p>
                          )}
                          <div className="">
                            <div className="d-flex align-items-baseline mb-2">
                              <span className="fw-bold" style={{ fontSize: '32px', color: '#2b61b1' }}>
                                £{parseFloat(plan.price || 0).toFixed(2)}
                              </span>
                              <span className="text-muted ms-1 small">/month</span>
                            </div>
                            <div className="d-flex align-items-center text-muted small mb-3">
                              <i className="fa fa-users me-2"></i>
                              <span>Up to <strong>{plan.student_limit}</strong> students</span>
                            </div>
                            {plan.addon_details && plan.addon_details.length > 0 && (
                              <div className="mb-3 p-2 bg-light rounded" style={{ border: '1px solid #e9ecef' }}>
                                <p className="small fw-bold mb-2" style={{ color: '#2b61b1' }}>Includes:</p>
                                {plan.addon_details.map((addon, index) => (
                                  <div key={index} className="d-flex align-items-center text-muted small mb-1">
                                    <i className="fa fa-check-circle me-2" style={{ color: '#28a745' }}></i>
                                    <span>{addon.addon_name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {!isChangingExistingPlan && (
                              <>
                                <div className="d-none d-flex align-items-center text-muted small mb-3">
                                  <i className="fa fa-pagelines me-2"></i>
                                  <span>14 Days Free Trial</span>
                                </div>
                                {(() => {
                                  // Parse plan's free addon IDs
                                  let planFreeAddonIds = [];
                                  if (plan.addons) {
                                    if (typeof plan.addons === 'string') {
                                      planFreeAddonIds = plan.addons.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
                                    } else if (Array.isArray(plan.addons)) {
                                      planFreeAddonIds = plan.addons;
                                    }
                                  }

                                  const showAddons = plan.addons && plan.addons.length > 0 && plan.company_id > 0
                                    ? addons.filter(a => plan.addons.includes(a.id))
                                    : addons.filter(a => a.is_active).filter(a => !planFreeAddonIds.includes(a.id));

                                  return showAddons.length > 0 && (
                                    <div className="border-top pt-3 mt-2" onClick={(e) => e.stopPropagation()}>
                                      <p className="text-muted small mb-2 fw-bold">Add-ons:</p>
                                      {showAddons.map((addon) => {
                                        const isPredefinedAddon = plan.addons && plan.addons.length > 0 && plan.company_id > 0;
                                        return (
                                          <div key={addon.id} className="form-check mb-2" onClick={(e) => e.stopPropagation()}>
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              id={`addon-${plan.id}-${addon.id}`}
                                              checked={selectedPlan?.id === plan.id && selectedAddons.includes(addon.id)}
                                              disabled={selectedPlan?.id !== plan.id || isPredefinedAddon}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                handleAddonToggle(addon.id);
                                              }}
                                            />
                                            <label
                                              className="form-check-label small d-flex justify-content-between align-items-center w-100"
                                              htmlFor={`addon-${plan.id}-${addon.id}`}
                                              style={{ 
                                                cursor: selectedPlan?.id === plan.id && !isPredefinedAddon ? 'pointer' : 'not-allowed',
                                                opacity: selectedPlan?.id === plan.id ? 1 : 0.6
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <span>{addon.addon_name}</span>
                                              {!isPredefinedAddon && (
                                                <span className="fw-bold ms-2" style={{ color: '#2b61b1' }}>
                                                  £{parseFloat(addon.price || 0).toFixed(0)}
                                                </span>
                                              )}
                                            </label>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                })}
                  {isCompanyUser && (
                    <>
                      {/* Bespoke Enquiry Box */}
                      <div
                        className="flex-shrink-0 me-3"
                        style={{ width: '320px' }}
                        onClick={handleBespokeEnquiryClick}
                      >
                        <div
                          className="card h-100 shadow-sm border-0"
                          style={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          }}
                        >
                          <div className="card-body d-flex flex-column p-4 text-white">
                            <div className="text-center mb-3">
                              <i className="fa fa-gem" style={{ fontSize: '48px' }}></i>
                            </div>
                            <h5 className="card-title fw-bold mb-2 text-center">
                              Bespoke Solution
                            </h5>
                            <p className="card-text small text-center mb-3" style={{ opacity: 0.9 }}>
                              Looking for a custom solution tailored to your specific needs? Let us create the perfect plan for you.
                            </p>
                            <div className="mt-auto">
                              <div className="text-center">
                                <span className="fw-bold" style={{ fontSize: '20px' }}>
                                  Custom Pricing
                                </span>
                              </div>
                              <div className="d-flex align-items-center text-center mt-2">
                                <i className="fa fa-arrow-right me-2"></i>
                                <span className="small">Click to Enquire</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <button
                  className="btn btn-light border-0 shadow-sm ms-2"
                  onClick={() => scrollPlans('right')}
                  style={{ minWidth: '40px', height: '40px', borderRadius: '50%' }}
                >
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            </div>
            {selectedPlan && Number(subscription?.plan_id ?? subscription?.plan?.id) !== Number(selectedPlan.id) && (
              <>
                <div className="form-check mt-3 p-3 ps-4" style={{ backgroundColor: '#e8f4ff', border: '1px solid #b8d9ff', borderRadius: '6px' }}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="student-limit-terms"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="student-limit-terms" style={{ fontSize: '0.95rem', fontWeight: 600, color: '#2b61b1' }}>
                    I understand that if my number of students exceeds the limit included in my selected plan, my subscription will automatically upgrade to the next applicable plan and the corresponding subscription price will apply.
                  </label>
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {viewMode === 'details' ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {(['active', 'trialing'].includes(subscription?.status?.toLowerCase()) && !subscription?.cancelled_at) && (
              <Button 
                variant="danger" 
                onClick={handleCancelSubscription}
                className="text-white"
              >
                <i className="fa fa-times-circle me-2"></i>
                Cancel Subscription
              </Button>
            )}
            <Button variant="outline-primary" onClick={switchToPlansView}>
              <i className="fa fa-crown me-2"></i>
              Upgrade/Change Plan
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            {isChangingExistingPlan && selectedPlan && 
             Number(subscription.plan_id ?? subscription.plan?.id) !== Number(selectedPlan.id) && (
              <Button
                variant="primary"
                onClick={handlePurchase}
                disabled={!selectedPlan || purchasing || !termsAccepted}
              >
                {purchasing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  (() => {
                    const currentPrice = parseFloat(subscription.plan?.price || 0);
                    const selectedPrice = parseFloat(selectedPlan?.price || 0);
                    const isUpgrade = selectedPrice > currentPrice;
                    const isDowngrade = selectedPrice < currentPrice;
                    
                    if (isUpgrade) {
                      return `Upgrade to ${selectedPlan ? selectedPlan.plan_name : 'plan'}`;
                    } else if (isDowngrade) {
                      return `Downgrade to ${selectedPlan ? selectedPlan.plan_name : 'plan'}`;
                    } else {
                      return `Update to ${selectedPlan ? selectedPlan.plan_name : 'plan'}`;
                    }
                  })()
                )}
              </Button>
            )}
            {!isChangingExistingPlan && (
              <Button
                variant="primary"
                onClick={handlePurchase}
                disabled={!selectedPlan || purchasing || !termsAccepted}
              >
                {purchasing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  `Purchase ${selectedPlan ? selectedPlan.plan_name : 'Plan'}`
                )}
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
    <BespokeEnquiryModal
      isOpen={showBespokeEnquiry}
      onClose={() => setShowBespokeEnquiry(false)}
      companyId={company_id}
    />
    
    {/* Addon Success Modal */}
    <Modal 
      show={showAddonSuccessModal} 
      backdrop="static" 
      keyboard={false}
      centered
      size="md"
    >
      <Modal.Header style={{ backgroundColor: '#2b61b1', color: 'white', border: 'none' }}>
        <Modal.Title style={{ fontSize: '18px' }}>
          <i className="fa fa-check-circle me-2"></i>
          Add-ons Updated Successfully
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div className="text-center mb-3">
          <i className="fa fa-check-circle text-success" style={{ fontSize: '60px' }}></i>
        </div>
        <div className="text-center mb-4">
          {addedAddonNames.length > 0 ? (
            <>
              <p className="mb-2" style={{ fontSize: '16px' }}>
                <strong>Great!</strong> The following add-ons are now available to you:
              </p>
              <div className="mt-3 p-3 bg-light rounded">
                {addedAddonNames.map((name, index) => (
                  <div key={index} className="mb-1">
                    <i className="fa fa-check text-success me-2"></i>
                    <strong>{name}</strong>
                  </div>
                ))}
              </div>
              <div className="text-center mb-3">
                <p className="mb-2" style={{ fontSize: '14px', color: '#666' }}>
                  Please configure these add-ons settings to start using them.
                </p>
                <a 
                  href="/fee-settings" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleAddonSuccessOk();
                  }}
                  style={{ 
                    color: '#2b61b1', 
                    textDecoration: 'underline', 
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Go to AddOns Management
                </a>
              </div>
            </>
          ) : (
            <>
              <p className="mb-2" style={{ fontSize: '16px' }}>
                <strong>Add-ons Updated Successfully</strong>
              </p>
              <p className="mb-2" style={{ fontSize: '14px', color: '#666' }}>
                Your subscription add-ons have been updated.
              </p>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer style={{ border: 'none' }}>
        <Button 
          variant="primary" 
          onClick={handleAddonSuccessOk}
          style={{ 
            backgroundColor: '#2b61b1', 
            borderColor: '#2b61b1',
            padding: '8px 30px'
          }}
        >
          OK
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default SubscriptionPlansModal;
